// Mount /dashboard, and keep it mounted across tab clicks.
//
// The server renders a shell for this page and nothing else (views/pages/DashboardShell.tsx
// in apps/pictufy); everything below the page chrome is rendered here. That is why tab
// clicks stop being page loads: the router swaps the view in place.
//
// This module stays in the entry bundle — main.ts imports it unconditionally, and it
// returns immediately on the 25 pages that are not the dashboard. Everything it renders is
// behind the ONE dynamic import below, so the ~1,900 lines of admin-only view code cost
// nothing to any other page.
import { registerClientRoute } from "../clientRoute";
import { api } from "../../api";
import { dashboardRoute, type DashboardRoute } from "./route";
import { startRouter, tryDashboardNavigate } from "./router";

/** The element the server's shell provides for us to render into. */
const ROOT_ID = "dashboard-root";

/**
 * The view, behind one dynamic import.
 *
 * One rather than one per tab: a page view renders at most one tab, so splitting further
 * would buy nothing and cost extra requests. Dynamic rather than static because this
 * module is reached from `main.ts`, and a top-level import would put every dashboard
 * table into the chunk all 26 pages download.
 */
const loadView = () => import("./view");

/**
 * Bumped on every navigation. A 21-second artists query that resolves after the user has
 * moved to resellers must not paint over it, and `renderRows` checks this before drawing.
 */
let generation = 0;

async function show(root: HTMLElement, route: DashboardRoute): Promise<void> {
  const mine = ++generation;

  // Started before the view is awaited so the chunk fetch and the query still overlap, as
  // they did when this was one `Promise.all`. The no-op catch only marks it handled for
  // the window before `await` reaches it — the `await` below still throws.
  const dataP = api.dashboardTab.load(route);
  dataP.catch(() => undefined);

  const view = await loadView();
  if (mine !== generation) return;
  // Tab bar with the clicked tab highlighted, spinner for the body. Six of the eleven tabs
  // are complete after `load` and so have no rows root: without this they sat showing the
  // previous tab for up to 1.4 s, which reads as a click that did nothing.
  view.renderPending(root, route.tab);

  const data = await dataP;
  if (mine !== generation) return;

  view.renderDashboard(root, data, () => mine === generation);
}

export function initDashboard(): void {
  const root = document.getElementById(ROOT_ID);
  if (!root) return;

  const initial = dashboardRoute(location.pathname);

  void show(root, initial)
    .then(() => {
      // Registered only after the first render succeeds. Until then a tab click is a
      // normal navigation, which is the right fallback: a dashboard that failed to mount
      // should still be escapable by clicking a tab.
      registerClientRoute(tryDashboardNavigate);
      startRouter((route) => void show(root, route), initial);
    })
    .catch((err: unknown) => {
      // Into a child, for the same reason the shell puts its spinner in one: the styling
      // classes belong to the message, not to the element the page mounts into.
      const message = document.createElement("div");
      message.className = "dashboard-tab-loading dashboard-tab-loading-done";
      message.textContent = "Could not load the dashboard. Reload to try again.";
      root.replaceChildren(message);
      console.error("dashboard: failed to load", err);
    });
}

/** Exported for the shell to keep in step; see DashboardShell.tsx. */
export { ROOT_ID as DASHBOARD_ROOT_ID };
